import { Button, Spacer } from '@odekoteam/doppio';
import { useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../hooks/store';
import {
  nightEndSelector,
  selectedWarehouseIdSelector,
} from '../../state/selectors/delivery';
import { modalSelector } from '../../state/selectors/modal';
import { markCustomerPickupStopsComplete } from '../../state/slices/delivery';
import { hideModal } from '../../state/slices/modal';
import CancelButton from './CancelButton';
import { PastNightPickedUpLine } from './MarkPickedUpModal';
import { buttonContainer, styledConfirmationModal } from './styles';

export const bulkMarkPickedUpTitle = (count: number): string =>
  `Mark ${count} order${count === 1 ? '' : 's'} as picked up?`;

// The design (sc-65865) requires the modal to state both consequences: the
// action is irreversible, and it bills.
export const bulkMarkPickedUpBody = (count: number): string =>
  `This can't be undone. The affected customer${
    count === 1 ? '' : 's'
  } will be invoiced on the next billing cycle.`;

export function BulkMarkPickedUpModal(): JSX.Element {
  const dispatch = useAppDispatch();
  const nightEnd = useAppSelector(nightEndSelector);
  const { variables } = useAppSelector(modalSelector);
  const selectedWarehouseId = useAppSelector(selectedWarehouseIdSelector);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const stopIds: string[] = variables?.stopIds ?? [];

  const handleOnClickConfirm = async (): Promise<void> => {
    if (stopIds.length === 0 || !selectedWarehouseId || !nightEnd?.viewing) {
      dispatch(hideModal());
      return;
    }
    setIsSubmitting(true);
    await dispatch(
      markCustomerPickupStopsComplete({
        stopIds,
        warehouseId: selectedWarehouseId,
        nightEnd: nightEnd.viewing,
      }),
    );
    setIsSubmitting(false);
    dispatch(hideModal());
  };

  return (
    <div
      id="bulk-mark-picked-up-modal"
      sx={{ ...styledConfirmationModal, textAlign: 'left' }}
    >
      <div sx={{ variant: 'text.headingSm' }}>
        {bulkMarkPickedUpTitle(stopIds.length)}
      </div>
      <Spacer height="$3" />
      <div sx={{ variant: 'text.paragraphMdDefault', color: 'secondaryText' }}>
        {bulkMarkPickedUpBody(stopIds.length)}
      </div>
      <PastNightPickedUpLine />
      <div sx={{ ...buttonContainer, justifyContent: 'flex-start' }}>
        <Button
          variant="primary"
          id="bulk-mark-picked-up-action-button"
          data-test-id="bulk-mark-picked-up-action-button"
          onPress={handleOnClickConfirm}
          isDisabled={isSubmitting}
        >
          {isSubmitting ? 'Marking...' : 'Mark as picked up'}
        </Button>
        <Spacer width="$2" />
        <CancelButton variant="secondary" />
      </div>
    </div>
  );
}
